import React, { useState, useEffect, useRef } from "react";
import { Box, Text, CircularProgress, Button } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import { useHistory, useLocation } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { useSelector, RootState, useDispatch } from "src/store";
import { update } from "src/store/auth";
import { PORTAL_ROUTE, SUBSCRIPTION_ROUTE } from "src/routePaths";
import DarkHeader from "src/layout/DarkHeader";
import { UserType } from "src/types";
import { getUserAskSalahSubscription } from "src/api/askSalah";
import {
  activateFreeTrial,
  fetchAuthUserMe,
  getFreeTrialVerifyStatus,
} from "src/api/subscription";

const PENDING_VERIFICATION_STATUSES = new Set([
  "PENDING",
  "INPROGRESS",
  "IN_PROGRESS",
  "INITIATED",
]);

const isPendingVerification = (
  verificationStatus:
    | { verified: boolean; status: string; authorizationId: string }
    | null
    | undefined
): boolean => {
  if (!verificationStatus) {
    return true;
  }
  if (verificationStatus.verified) {
    return false;
  }
  return PENDING_VERIFICATION_STATUSES.has(
    verificationStatus.status?.toUpperCase() ?? ""
  );
};

const FreeTrialCallback: React.FC = () => {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState<string>("");
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const { locale } = useIntl();
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 30; // 30 × 2s ≈ 60s for MyFatoorah inquiry lag
  const POLL_INTERVAL_MS = 2000;

  useEffect(() => {
    if (!user) {
      history.push(SUBSCRIPTION_ROUTE);
      return;
    }

    let cancelled = false;
    retryCountRef.current = 0;

    const processVerification = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const tapId = urlParams.get("tap_id");
        const myFatoorahPaymentId =
          urlParams.get("paymentId") ||
          urlParams.get("PaymentId") ||
          urlParams.get("id") ||
          urlParams.get("Id");

        let authorizationId =
          myFatoorahPaymentId ||
          tapId ||
          localStorage.getItem("freeTrialAuthorizationId");

        if (authorizationId) {
          localStorage.setItem("freeTrialAuthorizationId", authorizationId);
        }

        if (!authorizationId) {
          setStatus("error");
          setMessage("Verification session expired. Please try again.");
          return;
        }

        const verificationStatus = await getFreeTrialVerifyStatus(
          authorizationId,
          locale,
        );

        if (isPendingVerification(verificationStatus)) {
          retryCountRef.current += 1;
          if (retryCountRef.current <= MAX_RETRIES && !cancelled) {
            setTimeout(() => {
              if (!cancelled) {
                processVerification();
              }
            }, POLL_INTERVAL_MS);
            return;
          }

          setStatus("error");
          setMessage(
            "Verification is taking longer than expected. Please check back in a few moments or try again."
          );
          localStorage.removeItem("freeTrialAuthorizationId");
          return;
        }

        if (verificationStatus!.verified) {
          try {
            const result = await activateFreeTrial(authorizationId, locale);

            if (result.success) {
              localStorage.removeItem("freeTrialAuthorizationId");

              try {
                const freshUserData: UserType = await fetchAuthUserMe(locale);

                if (freshUserData?.id && user?.accessToken) {
                  dispatch(update({
                    user: { ...freshUserData, accessToken: user.accessToken },
                  }));

                  try {
                    const askSalahSubscription = await getUserAskSalahSubscription();
                    if (askSalahSubscription) {
                      dispatch(update({
                        user: {
                          ...freshUserData,
                          accessToken: user.accessToken,
                          askSalahSubscription: askSalahSubscription,
                        },
                      }));
                    }
                  } catch (askSalahError) {
                    console.error("Failed to refresh AskSalah subscription:", askSalahError);
                  }
                }
              } catch (refreshError) {
                console.error("Failed to refresh user data:", refreshError);
              }

              setStatus("success");
              setMessage("Card verified! Your free trial subscription is now active.");

              toast({
                title: "Free trial activated successfully",
                status: "success",
                isClosable: true,
              });

              setTimeout(() => {
                history.push(PORTAL_ROUTE);
              }, 2000);
            } else {
              setStatus("error");
              setMessage(result.message || "Failed to activate free trial");
            }
          } catch (subscriptionError: any) {
            setStatus("error");
            setMessage(
              subscriptionError.message ||
                "Failed to create subscription. Please try again."
            );
          }
        } else {
          setStatus("error");
          setMessage(
            `Card verification failed: ${verificationStatus!.status}. Please try again with a different card.`
          );
          localStorage.removeItem("freeTrialAuthorizationId");
        }
      } catch (error: any) {
        console.error("Verification callback error:", error);
        setStatus("error");
        setMessage(
          error.message || "An error occurred during verification. Please try again."
        );
        localStorage.removeItem("freeTrialAuthorizationId");
      }
    };

    processVerification();

    return () => {
      cancelled = true;
    };
  }, [history, location.search, locale, user?.accessToken, dispatch, toast]);

  return (
    <>
      <DarkHeader />
      <Box paddingTop="200px" textAlign="center">
        {status === "loading" && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            paddingBottom="300px"
          >
            <CircularProgress isIndeterminate color="brand.900" size="60px" />
            <Text color="gray" marginTop="20px" fontSize="18px">
              <FormattedMessage id="processing" defaultMessage="Processing..." />
            </Text>
            <Text color="gray" marginTop="10px" fontSize="14px">
              Verifying your card...
            </Text>
          </Box>
        )}

        {status === "success" && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            paddingBottom="300px"
          >
            <FiCheckCircle color="green" size={52} />
            <Text color="green" marginTop="20px" fontWeight="bold" fontSize="18px">
              {message || "Card verified successfully!"}
            </Text>
            <Text color="gray" marginTop="10px" fontSize="14px">
              Redirecting to portal...
            </Text>
          </Box>
        )}

        {status === "error" && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            paddingBottom="300px"
          >
            <FiAlertCircle color="red" size={52} />
            <Text color="red" marginTop="20px" fontWeight="bold" fontSize="18px">
              {message || "Verification failed"}
            </Text>
            <Button
              marginTop="20px"
              backgroundColor="brand.900"
              color="white"
              onClick={() => {
                localStorage.removeItem("freeTrialAuthorizationId");
                history.push(SUBSCRIPTION_ROUTE);
              }}
            >
              <FormattedMessage id="tryAgain" defaultMessage="Try Again" />
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default FreeTrialCallback;
